import React, { useContext, useEffect, useState } from 'react';
import { RadioGroup } from '@one-thd/sui-atomic-components';
import { RETURN_METHOD } from '@src/constants/general';
import { getReturnEligibilityDetails, setReturnMethod, useReturnStore, } from '@src/stores/returnStore';
import { disableAbandonmentSurvey, enableLORPAbandonmentSurvey, enableReturnAbandonmentSurvey, } from '@src/utils/storage-helper';
import { TagReturnMethodLoad } from '@src/utils/analytics-tagging';
import { goToNextStep, useFlowStore } from '@src/stores/flowStore';
import { AppContext } from '@thd-nucleus/app-render';
import { buildReturnEligibilityRequest } from '@src/utils';
import { getFeatureSwitchStatic, getWindow, isFeatureSwitchEnabled, } from '@src/utils/general';
import SingleInStoreOption from './SingleInStoreOption';
import { getReturnMethodEligibility, InStoreReturnMethodDescription, LORPReturnMethodDescription, ReturnMethodOption, ReturnMethodSkeletonLoader, UpsDropOffReturnMethodDescription, } from './ReturnMethodHelper';
import { ReturnButtons } from '../shared/ReturnButtons';
var ReturnMethodOptions = function (_a) {
    var _b;
    var fetchData = _a.fetchData, isFetched = _a.isFetched, loading = _a.loading, error = _a.error;
    var instance = useContext(AppContext).instance;
    var _c = useState('homedepot.com'), hostname = _c[0], setHostname = _c[1];
    var _d = useState((_b = {},
        _b[RETURN_METHOD.IN_STORE] = true,
        _b[RETURN_METHOD.UPS_DROP_OFF] = true,
        _b[RETURN_METHOD.LORP] = false,
        _b)), methodDisplayStatus = _d[0], setMethodDisplayStatus = _d[1];
    var _e = useState(''), lastChosenMethod = _e[0], setLastChosenMethod = _e[1];
    var returnFlow = useFlowStore(function (state) { return state.returnFlow; });
    var returnMethod = useReturnStore(function (state) { return state.returnMethod; });
    var returnEligibilityDetails = useReturnStore(function (state) { return state.returnEligibilityDetails; });
    var isMilitaryExchangeGuest = useReturnStore(function (state) { return state.isMilitaryExchangeGuest; });
    var militaryExchange = useReturnStore(function (state) { return state.militaryExchange; });
    var orderDetailsV2 = useReturnStore(function (state) { return state.orderDetailsV2; });
    var isDynamicReturnMethodEnabled = Boolean(getFeatureSwitchStatic('isDynamicReturnMethodEnabled', true, true));
    var isCashNCarryOrderNumber = orderDetailsV2 === null || orderDetailsV2 === void 0 ? void 0 : orderDetailsV2.isCashNCarryOrderNumber;
    var allowSORIS = getWindow() && isFeatureSwitchEnabled(instance, 'allowSORIS');
    var enableSCAC = getWindow() && isFeatureSwitchEnabled(instance, 'enableSCAC');
    var isLORPEnabled = Boolean(getFeatureSwitchStatic('isLORPEnabled', true, false));
    var items = (orderDetailsV2 === null || orderDetailsV2 === void 0 ? void 0 : orderDetailsV2.lineItems) || [];
    var callCheckEligibility = function (populatedOrderDetails) {
        var requestData = buildReturnEligibilityRequest(populatedOrderDetails, isMilitaryExchangeGuest, items, enableSCAC);
        fetchData(function () { return getReturnEligibilityDetails(requestData); }).then(function (_a) {
            var _b;
            var returnEligibilityData = _a.data;
            var _c = getReturnMethodEligibility(populatedOrderDetails, returnEligibilityData), returnInStoreAllowed = _c.returnInStoreAllowed, returnDropoffAllowed = _c.returnDropoffAllowed, returnLORPAllowed = _c.returnLORPAllowed;
            // Set the display status for the methods after calculating eligibilities
            setMethodDisplayStatus((_b = {},
                _b[RETURN_METHOD.IN_STORE] = returnInStoreAllowed,
                _b[RETURN_METHOD.UPS_DROP_OFF] = returnDropoffAllowed,
                _b[RETURN_METHOD.LORP] = returnLORPAllowed,
                _b));
        });
    };
    var handleMethodChange = function (event) {
        var selectedMethod = event.target
            .value;
        setReturnMethod(selectedMethod);
        if (selectedMethod === RETURN_METHOD.LORP) {
            enableLORPAbandonmentSurvey();
            return;
        }
        if (selectedMethod === RETURN_METHOD.UPS_DROP_OFF) {
            enableReturnAbandonmentSurvey();
            return;
        }
        disableAbandonmentSurvey();
    };
    var handleContinueButton = function () {
        if (isCashNCarryOrderNumber) {
            // If the order is a return receipt order, skip eligibility and continue
            goToNextStep('ReturnMethod');
            return;
        }
        if (!isDynamicReturnMethodEnabled) {
            if (allowSORIS || returnMethod === RETURN_METHOD.UPS_DROP_OFF) {
                if (orderDetailsV2 &&
                    (!returnEligibilityDetails || lastChosenMethod !== returnMethod)) {
                    var requestData = buildReturnEligibilityRequest(orderDetailsV2, isMilitaryExchangeGuest, items, enableSCAC);
                    getReturnEligibilityDetails(requestData);
                }
            }
            setLastChosenMethod(returnMethod);
        }
        goToNextStep('ReturnMethod');
    };
    var handleRetryButton = function () {
        return orderDetailsV2 && callCheckEligibility(orderDetailsV2);
    };
    useEffect(function () {
        var _a;
        if (orderDetailsV2) {
            // On-load analytics tagging
            TagReturnMethodLoad(militaryExchange);
            setHostname(((_a = getWindow()) === null || _a === void 0 ? void 0 : _a.location.hostname) || 'homedepot.com');
            // Call return eligibility on load if dynamic methods FS is enabled
            if (!isCashNCarryOrderNumber && isDynamicReturnMethodEnabled) {
                callCheckEligibility(orderDetailsV2);
            }
        }
    }, [orderDetailsV2, militaryExchange]);
    if (isCashNCarryOrderNumber) {
        return (React.createElement(SingleInStoreOption, { handleContinueButton: handleContinueButton, handleMethodChange: handleMethodChange, hostname: hostname }));
    }
    if (isDynamicReturnMethodEnabled && (!isFetched || loading)) {
        return React.createElement(ReturnMethodSkeletonLoader, null);
    }
    if (error) {
        return (React.createElement(ReturnButtons, { isError: true, proceedNext: handleRetryButton, disabled: !isFetched || loading }));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(RadioGroup, { value: returnMethod },
            React.createElement("div", { className: "sui-flex sui-flex-col sui-gap-4" },
                (!isDynamicReturnMethodEnabled ||
                    methodDisplayStatus[RETURN_METHOD.IN_STORE]) && (React.createElement(ReturnMethodOption, { returnMethodName: RETURN_METHOD.IN_STORE, returnMethodHeader: "In-Store Drop-Off", returnMethodDescription: InStoreReturnMethodDescription(hostname, isMilitaryExchangeGuest), onChange: handleMethodChange })),
                (!isDynamicReturnMethodEnabled ||
                    methodDisplayStatus[RETURN_METHOD.UPS_DROP_OFF]) && (React.createElement(ReturnMethodOption, { returnMethodName: RETURN_METHOD.UPS_DROP_OFF, returnMethodHeader: "UPS Drop-Off", returnMethodDescription: UpsDropOffReturnMethodDescription, onChange: handleMethodChange })),
                isLORPEnabled &&
                    (!isDynamicReturnMethodEnabled ||
                        methodDisplayStatus[RETURN_METHOD.LORP]) && (React.createElement(ReturnMethodOption, { returnMethodName: RETURN_METHOD.LORP, returnMethodHeader: "Large Item Pickup", returnMethodDescription: LORPReturnMethodDescription, onChange: handleMethodChange })))),
        React.createElement(ReturnButtons, { proceedNext: handleContinueButton, disabled: isDynamicReturnMethodEnabled &&
                (Boolean(error) || !isFetched || loading) })));
};
export default ReturnMethodOptions;
